import { useTranslation } from '@thoughtspot/i18n';
import {
    Alert,
    BannerAlertType,
} from '@thoughtspot/radiant-react/widgets/alert';
import { FormBuilder } from '@thoughtspot/radiant-react/widgets/form-builder';
import { FormBuilderHandles } from '@thoughtspot/radiant-react/widgets/form-builder/form-builder';
import { FormField } from '@thoughtspot/radiant-react/widgets/form-builder/interface';
import { Vertical } from '@thoughtspot/radiant-react/widgets/layout';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import React, { useEffect, useRef, useState } from 'react';
import { TranslationsKeys } from '../constants';
import { getAddEditOrgElementsConfig } from '../utils';
import styles from './create-edit-modal.module.scss';

export interface CreateEditModalProps {
    isModalOpened: boolean;
    closeModal: () => void;
    showError?: any;
    setShowError?: (err: any) => void;
    onSubmit?: (data: any) => void;
    title?: string;
    confirmButton?: string;
    loader?: boolean;
    name?: string;
    description?: string;
}

export const CreateEditModal: React.FC<CreateEditModalProps> = ({
    isModalOpened,
    closeModal,
    showError = null,
    setShowError,
    onSubmit,
    title = null,
    confirmButton = null,
    loader = false,
    name = null,
    description = null,
}: CreateEditModalProps) => {
    const { t } = useTranslation();
    const formBuilderRef = useRef<FormBuilderHandles>(null);

    const getUpdatedFormField = (
        currentFormData: FormField[] = getAddEditOrgElementsConfig(t),
    ): FormField[] => {
        const newFormFields: FormField[] = [];

        currentFormData.forEach((field: any) => {
            let value: any = field.value || '';
            const config: any = field.config;
            if (
                field.id === 'name' &&
                name &&
                name.length !== 0 &&
                name !== 'undefined'
            ) {
                value = name;
            } else if (
                description &&
                description.length !== 0 &&
                description !== 'undefined'
            ) {
                value = description;
            }
            newFormFields.push({
                ...field,
                ...config,
                value,
            });
        });
        return newFormFields;
    };

    const [formFields, setFormFields] = useState<FormField[]>(
        getUpdatedFormField(),
    );

    const updateFormFields = (
        currentFormData: FormField[] = getAddEditOrgElementsConfig(t),
    ) => {
        const newFormFields: FormField[] = getUpdatedFormField(currentFormData);
        setFormFields(newFormFields);
    };

    useEffect(() => {
        updateFormFields();
    }, []);

    const submitForm = () => {
        const currentFormData = formBuilderRef.current.validateAndSubmitForm();
        const allValid =
            currentFormData.filter((field: any) => field.valid === false)
                .length < 1;
        if (allValid) {
            const formValues: any = {};
            currentFormData.forEach((field: any) => {
                formValues[field.id] = field.value || '';
            });
            onSubmit(formValues);
        }
    };

    const onDismissModal = () => {
        setShowError(null);
        closeModal();
    };

    const onConfirmModal = () => {
        submitForm();
    };

    const modalProps = {
        size: ModalSize.XSMALL,
        title: title || t(TranslationsKeys.CREATE_MODAL_TITLE),
        onDismiss: onDismissModal,
        isOpen: isModalOpened,
        confirmButton: {
            text: confirmButton || t(TranslationsKeys.CREATE_MODAL_SAVE),
            onConfirm: onConfirmModal,
            isDisabled: showError || loader,
        },
        dismissButton: {
            text: t(TranslationsKeys.CREATE_MODAL_CANCEL),
        },
        autoScrollContent: false,
    };

    return (
        <>
            <Modal {...modalProps}>
                <Vertical hAlignContent="stretch" grow>
                    <Alert.Banner
                        message={showError}
                        isVisible={!!showError}
                        type={BannerAlertType.ERROR}
                        className={styles.alertBanner}
                        onClose={() => setShowError(null)}
                    />
                    <div
                        role="presentation"
                        data-testid="collectionsModal_add"
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (
                                e.key === 'Enter' &&
                                !(e.target instanceof HTMLTextAreaElement) &&
                                !showError &&
                                !loader
                            ) {
                                e.preventDefault();
                                submitForm();
                            }
                        }}
                    >
                        <FormBuilder
                            ref={formBuilderRef}
                            formFields={formFields as any}
                        />
                    </div>
                </Vertical>
            </Modal>
        </>
    );
};
